import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, X, Shield, Trash2, Copy, Check, ChevronRight, GripVertical } from "lucide-react";
import { toast } from "sonner";
import RoleSettingsPanel from "./RoleSettingsPanel";

export default function TeamsTab({ token, eventId, eventName, isAdmin, onCountChange, showConfirm, closeConfirm }) {
  const [roles, setRoles] = useState([]);
  const [mainRoles, setMainRoles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({ name: "", description: "", permissions: [] });
  const [copyingRoleId, setCopyingRoleId] = useState(null);
  const [copyName, setCopyName] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  // Sammenklap/udvid pr. team (kun ét eller flere åbne ad gangen)
  const [expandedTeamIds, setExpandedTeamIds] = useState(() => new Set());
  // Manuelt valgt rækkefølge — gemmes lokalt pr. event
  const orderKey = `teamOrder:${eventId}`;
  const [teamOrder, setTeamOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(orderKey) || '[]'); } catch { return []; }
  });
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const toggleTeamExpanded = (id) => {
    setExpandedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Sortér roller efter gemt rækkefølge (ukendte teams placeres til sidst i original rækkefølge)
  const sortedRoles = (() => {
    if (!Array.isArray(roles)) return [];
    const idxMap = new Map();
    teamOrder.forEach((id, i) => idxMap.set(id, i));
    return [...roles].sort((a, b) => {
      const ai = idxMap.has(a.id) ? idxMap.get(a.id) : Infinity;
      const bi = idxMap.has(b.id) ? idxMap.get(b.id) : Infinity;
      if (ai !== bi) return ai - bi;
      return 0;
    });
  })();

  const persistOrder = (newOrder) => {
    setTeamOrder(newOrder);
    try { localStorage.setItem(orderKey, JSON.stringify(newOrder)); } catch {}
  };

  const handleDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch {}
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverId) setDragOverId(id);
  };
  const handleDragLeave = (e) => { setDragOverId(null); };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDragOverId(null);
    if (!fromId || fromId === targetId) return;
    const currentIds = sortedRoles.map(r => r.id).filter(Boolean);
    const fromIdx = currentIds.indexOf(fromId);
    const toIdx = currentIds.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const newOrder = [...currentIds];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    persistOrder(newOrder);
  };
  const handleDragEnd = () => { setDraggingId(null); setDragOverId(null); };

  const availablePermissions = [
    { key: "view_map", label: "Se kort" },
    { key: "view_zones", label: "Se zoner" },
    { key: "view_resource_points", label: "Se ressourceøer" },
    { key: "view_toilets", label: "Se toiletter" },
    { key: "register_waste", label: "Registrer affald" },
  ];

  const loadRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/events/${eventId}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setRoles(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error("Fejl ved hentning af roller", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMainRoles = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/main-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMainRoles(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af hovedroller", error);
    }
  };

  const loadShifts = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af vagter", error);
    }
  };

  useEffect(() => {
    loadRoles();
    loadMainRoles();
    loadShifts();
  }, [eventId]);

  const resetRoleForm = () => { setNewRole({ name: "", description: "", permissions: [] }); setEditingRole(null); };

  const saveRole = async () => {
    if (!newRole.name.trim()) { toast.error("Indtast et rolle-navn"); return; }
    try {
      if (editingRole) {
        await axios.put(`${API}/events/${eventId}/roles/${editingRole.id}`, newRole, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Rolle opdateret!");
      } else {
        await axios.post(`${API}/events/${eventId}/roles`, { name: newRole.name, description: newRole.description || '' }, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Team oprettet!");
      }
      setShowAddRoleForm(false);
      resetRoleForm();
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gemning af rolle");
    }
  };

  const deleteRole = (roleId, roleName) => {
    showConfirm("Slet team?", `Er du sikker på du vil slette "${roleName}"?`, async () => {
      try {
        await axios.delete(`${API}/events/${eventId}/roles/${roleId}`, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Rolle slettet");
        loadRoles();
      } catch (error) {
        toast.error("Fejl ved sletning af rolle");
      }
      closeConfirm();
    });
  };

  const copyTeam = async (roleId) => {
    if (!copyName.trim()) { toast.error("Indtast et navn til den nye kopi"); return; }
    setCopyLoading(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/roles/${roleId}/copy`,
        { name: copyName.trim() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Team "${copyName.trim()}" oprettet!`);
      setCopyingRoleId(null);
      setCopyName('');
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved kopiering af team");
    } finally {
      setCopyLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Teams</h2>
          <p className="text-sm text-muted-foreground">Administrer teams og rettigheder for {eventName}</p>
        </div>
        <Button onClick={() => setShowAddRoleForm(true)} className="bg-primary text-primary-foreground" data-testid="add-team-btn">
          <Plus className="h-4 w-4 mr-2" />Opret nyt team
        </Button>
      </div>

      {showAddRoleForm && !editingRole && (
        <div className="p-4 bg-card border border-border rounded-lg">
          <h3 className="font-bold mb-4">Opret nyt team</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Teamnavn *</Label><Input placeholder="F.eks. Sweeper Zone A" value={newRole.name} onChange={(e) => setNewRole({...newRole, name: e.target.value})} data-testid="new-team-name" /></div>
              <div><Label>Beskrivelse</Label><Input placeholder="Kort beskrivelse af teamet" value={newRole.description} onChange={(e) => setNewRole({...newRole, description: e.target.value})} /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveRole} data-testid="save-team-btn"><Save className="h-4 w-4 mr-2" />Opret team</Button>
              <Button variant="outline" onClick={() => { setShowAddRoleForm(false); setEditingRole(null); resetRoleForm(); }}><X className="h-4 w-4 mr-2" />Annuller</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Henter teams...</div>
      ) : roles.length === 0 ? (
        <div className="text-center py-12 bg-card/30 rounded-lg border border-dashed border-border">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Ingen teams defineret</h3>
          <p className="text-muted-foreground mb-4">Opret dit første team for at komme i gang</p>
          <Button onClick={() => setShowAddRoleForm(true)}><Plus className="h-4 w-4 mr-2" />Opret team</Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {sortedRoles.map((role, idx) => {
            const teamId = role.id || `role-${idx}`;
            const isExpanded = expandedTeamIds.has(teamId);
            const isDragOver = dragOverId === teamId && draggingId !== teamId;
            return (
            <div
              key={teamId}
              draggable={!!role.id}
              onDragStart={(e) => handleDragStart(e, teamId)}
              onDragOver={(e) => handleDragOver(e, teamId)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, teamId)}
              onDragEnd={handleDragEnd}
              className={`bg-card border rounded-lg transition-all ${
                isDragOver ? 'border-primary border-2 shadow-lg' : 'border-border hover:border-primary/50'
              } ${draggingId === teamId ? 'opacity-50' : ''}`}
              data-testid={`team-card-${teamId}`}
            >
              {/* Sammenklappelig header */}
              <div className="p-3 flex items-center gap-2">
                {role.id && (
                  <div
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1"
                    title="Træk for at ændre rækkefølge"
                  >
                    <GripVertical className="h-4 w-4" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleTeamExpanded(teamId)}
                  className="flex-1 flex items-center gap-2 text-left min-w-0"
                  data-testid={`team-toggle-${teamId}`}
                >
                  <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  <h3 className="font-bold text-base truncate">{role.name || role['Rolle'] || role['rolle'] || 'Unavngivet'}</h3>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${role.source === 'excel_import' ? 'bg-green-500/20 text-green-400' : role.source === 'excel_legacy' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {role.source === 'excel_import' ? 'Import' : role.source === 'excel_legacy' ? 'Gammel Excel' : 'Manuel'}
                  </span>
                  {!isExpanded && (role.description || role['Kort arbejdsbeskrivelse']) && (
                    <span className="text-xs text-muted-foreground truncate hidden sm:inline">
                      · {role.description || role['Kort arbejdsbeskrivelse']}
                    </span>
                  )}
                </button>
                {role.can_edit && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setCopyingRoleId(copyingRoleId === role.id ? null : role.id); setCopyName(`${role.name || 'Team'} kopi`); }} title="Kopier team" data-testid={`copy-team-${role.id}`}><Copy className="h-4 w-4" /></Button>
                    <Button variant="destructive" size="sm" onClick={(e) => { e.stopPropagation(); deleteRole(role.id, role.name); }} title="Slet rolle"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>

              {/* Selve indholdet — kun renderet når udvidet */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-border/50">
                  <p className="text-sm text-muted-foreground mb-3 mt-3">{role.description || role['Kort arbejdsbeskrivelse'] || 'Ingen beskrivelse'}</p>
                  {isAdmin ? (
                    <RoleSettingsPanel role={role} eventId={eventId} token={token} availablePermissions={availablePermissions} mainRoles={mainRoles} allRoles={roles} shifts={shifts} onUpdate={() => { loadRoles(); loadMainRoles(); }} />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {role.permissions?.length > 0 ? (
                        role.permissions.map(perm => <span key={perm} className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">{availablePermissions.find(p => p.key === perm)?.label || perm}</span>)
                      ) : (
                        <>
                          {role['Kan se kort'] === 'Ja' && <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Se kort</span>}
                          {role['Kan se Zoner'] === 'Ja' && <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Se zoner</span>}
                          {role['Kan se ressourceøer'] === 'Ja' && <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Se ressourceøer</span>}
                          {role['Kan se toiletter'] === 'Ja' && <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Se toiletter</span>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {copyingRoleId === role.id && (
                <div className="mx-4 mb-4 p-3 bg-muted/30 rounded-lg border border-primary/30" data-testid="copy-team-form">
                  <p className="text-sm font-medium mb-2">Kopier "{role.name}" med alle indstillinger</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nyt teamnavn"
                      value={copyName}
                      onChange={(e) => setCopyName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') copyTeam(role.id); }}
                      className="flex-1"
                      autoFocus
                      data-testid="copy-team-name-input"
                    />
                    <Button size="sm" onClick={() => copyTeam(role.id)} disabled={copyLoading} data-testid="copy-team-confirm">
                      {copyLoading ? 'Kopierer...' : <><Check className="h-4 w-4 mr-1" />Kopier</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setCopyingRoleId(null); setCopyName(''); }}><X className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Alle rettigheder, notifikationer, vagter og andre indstillinger kopieres med</p>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
